import type { ReactElement } from 'react';

/**
 * The console's small icon set (design review 30 Sep): 24×24 line icons drawn in `currentColor`,
 * 1.75px stroke, shown at 16px (`.icon`). Inline SVG — no icon font, no dependency. Only where
 * an icon earns its place: the sidebar (one per screen, so the menu can be scanned by shape),
 * the user chip's chevron, and the user menu. Status icons (banners, toasts, field errors) are
 * drawn by CSS (`--i-*` in styles.css) so every screen gets them without a markup change.
 *
 * Always decorative: `aria-hidden`, never the only carrier of meaning — every icon sits next to
 * the text that names the thing, and that text stays the accessible name.
 */
export type IconName =
  | 'dash' | 'monitor' | 'report' | 'log' | 'investigate'
  | 'rb' | 'pb' | 'rulesets' | 'fields'
  | 'masters' | 'alert' | 'users' | 'apikeys'
  | 'chevron-down' | 'key' | 'logout';

const PATHS: Record<IconName, ReactElement> = {
  // Monitoring
  dash: (
    <>
      <rect x="3.5" y="3.5" width="7" height="8" rx="1" />
      <rect x="13.5" y="3.5" width="7" height="5" rx="1" />
      <rect x="13.5" y="11.5" width="7" height="9" rx="1" />
      <rect x="3.5" y="14.5" width="7" height="6" rx="1" />
    </>
  ),
  monitor: <path d="M3 12h4l2.5-6 5 12L17 12h4" />,
  report: (
    <>
      <path d="M14 3.5H7a1.5 1.5 0 0 0-1.5 1.5v14A1.5 1.5 0 0 0 7 20.5h10a1.5 1.5 0 0 0 1.5-1.5V8L14 3.5Z" />
      <path d="M14 3.5V8h4.5M9 17v-3M12 17v-5M15 17v-2" />
    </>
  ),
  log: <path d="M9 6h11M9 12h11M9 18h11M4.5 6h.01M4.5 12h.01M4.5 18h.01" />,
  // Investigate
  investigate: (
    <>
      <circle cx="10.5" cy="10.5" r="6" />
      <path d="m15 15 5.5 5.5" />
    </>
  ),
  // Rules
  rb: (
    <>
      <rect x="3" y="5.5" width="18" height="13" rx="1.5" />
      <path d="M3 9.5h18M6.5 14.5h4" />
    </>
  ),
  pb: (
    <>
      <path d="M4 18V14M9 18V9M14 18v-6M19 18V5" />
      <path d="M3 20.5h18" />
    </>
  ),
  rulesets: (
    <>
      <path d="m12 3.5 8.5 4.5-8.5 4.5L3.5 8 12 3.5Z" />
      <path d="m3.5 12 8.5 4.5 8.5-4.5M3.5 16l8.5 4.5 8.5-4.5" />
    </>
  ),
  fields: (
    <>
      <rect x="3.5" y="4.5" width="17" height="15" rx="1.5" />
      <path d="M3.5 9.5h17M9.5 9.5v10" />
    </>
  ),
  // Admin
  masters: (
    <>
      <ellipse cx="12" cy="6" rx="7.5" ry="2.5" />
      <path d="M4.5 6v12c0 1.4 3.4 2.5 7.5 2.5s7.5-1.1 7.5-2.5V6M4.5 12c0 1.4 3.4 2.5 7.5 2.5s7.5-1.1 7.5-2.5" />
    </>
  ),
  alert: (
    <>
      <path d="M6 16.5V11a6 6 0 0 1 12 0v5.5l1.5 2h-15l1.5-2Z" />
      <path d="M10 20.5a2 2 0 0 0 4 0" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8.5" r="3.5" />
      <path d="M2.5 20c.6-3.4 3.2-5.5 6.5-5.5s5.9 2.1 6.5 5.5" />
      <path d="M16 5.2a3.5 3.5 0 0 1 0 6.6M18 14.8c2 .7 3.2 2.5 3.5 5.2" />
    </>
  ),
  apikeys: (
    <>
      <circle cx="8" cy="15" r="4.5" />
      <path d="m11.2 11.8 8.3-8.3M16.5 6.5l2.5 2.5M14 9l2 2" />
    </>
  ),
  // Chrome
  'chevron-down': <path d="m6 9 6 6 6-6" />,
  key: (
    <>
      <rect x="4.5" y="10.5" width="15" height="10" rx="1.5" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3M12 14.5v2" />
    </>
  ),
  logout: <path d="M14 4.5h4A1.5 1.5 0 0 1 19.5 6v12a1.5 1.5 0 0 1-1.5 1.5h-4M10 16.5 5.5 12 10 7.5M5.5 12H15" />,
};

export function Icon({ name, size = 16, className }: { name: IconName; size?: number; className?: string }) {
  return (
    <svg
      className={className ? `icon ${className}` : 'icon'}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}

/** The sidebar icon of each screen id (`navigation.ts` ids); a screen without one gets none. */
export function navIcon(id: string): IconName | undefined {
  return (id in PATHS ? id : undefined) as IconName | undefined;
}
